/* SavvyShopify.formatMoney(cents, format) - formats an amount in cents through
   Shopify's `money_format` template syntax (https://shopify.dev/docs/api/liquid/filters/money-filters).

   Not `Shopify.formatMoney`: that name isn't actually provided by the Shopify
   platform for Online Store 2.0 themes (that was a legacy `option_selection.js`/
   `shopify_common.js` convention Shopify no longer auto-injects). On this store it
   only exists where an app embed happens to load Shopify's `api.jquery.js` (the
   Wizybot chat embed, on the live theme; themes without that embed don't have it),
   or where `quick-view-render.js` defines a fallback - and that file isn't
   `<script>`-included anywhere in the theme. Namespaced under `SavvyShopify`
   instead of patching onto `window.Shopify` so this doesn't collide with - or get
   mistaken for - whichever version happens to be present on a given page.

   Ported from https://gist.github.com/stewartknapman/8d8733ea58d2314c373e94114472d44c
   (the standard community implementation of this exact algorithm), verified against
   this store's real ${{amount_with_comma_separator}} format and a real discounted
   price before adoption. Known gaps carried over from that source, neither hit by
   this store's actual currency settings: a negative amount places the sign after
   the currency symbol ("$-5.00"), and `amount_with_apostrophe_separator` isn't
   handled (falls through to an empty value). */
window.SavvyShopify = window.SavvyShopify || {};

window.SavvyShopify.money_format = '${{amount}}';

window.SavvyShopify.formatMoney = function (cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  var value = '';
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = format || this.money_format;

  function defaultOption(opt, def) {
    return typeof opt === 'undefined' ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal = defaultOption(decimal, '.');

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var centsPart = parts[1] ? decimal + parts[1] : '';

    return dollars + centsPart;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
};

/* SavvyShopify.formatMoneyLike(cents, exampleText, exampleCents) - formats `cents`
   the way the server already formatted `exampleText` for `exampleCents`, and
   returns null when it cannot work that out.

   Why this exists rather than another call to formatMoney() above: the placeholder
   mapping that function implements is Shopify's documented one, but it is not what
   this store actually renders. `shop.money_format` here is `${{amount_no_decimals}}`,
   whose documented output groups with commas - and the storefront serves "$101.575",
   "$124.900", with dots. Formatting the discounted half of a strike-through through
   the template therefore produced "$184.900" struck against "$166,410" on one line.
   Rather than hardcode a second guess about this store's currency (a dot today,
   wrong again the moment a market or currency setting changes), this derives the
   format from a worked example the server itself produced: the pre-discount price
   is sitting in the element, and its value in cents is on the element beside it.

   Only the number is rebuilt. Everything around it in the example - currency symbol,
   currency code, whichever spaces sit between them - is reused verbatim, so nothing
   about the shape of the string has to be understood, only the digits.

   Returns null rather than falling back when the example cannot be read: a price
   nobody can format correctly is one this theme declines to render, which costs a
   strike-through. A wrong one costs the shopper's trust in the number. */
window.SavvyShopify.formatMoneyLike = function (cents, exampleText, exampleCents) {
  if (!isFinite(cents) || cents < 0) return null;
  if (typeof exampleText !== 'string') return null;
  if (!isFinite(exampleCents) || exampleCents <= 0) return null;

  /* The run of digits and their separators. Anchored on a digit at both ends so a
     trailing currency code ("$184.900 COP") stays in the suffix where it belongs. */
  var match = exampleText.match(/\d[\d.,\u00a0\u202f' ]*\d|\d/);
  if (!match) return null;

  var numeric = match[0];
  var prefix = exampleText.slice(0, match.index);
  var suffix = exampleText.slice(match.index + numeric.length);
  var digits = numeric.replace(/\D/g, '');
  var separators = numeric.replace(/\d/g, '');

  /* Which of the two the example is decides everything else: Shopify counts money
     in minor units regardless of how many the currency displays, so 18490000 is
     either "184.900" (none shown) or "184900.00" (two). Anything else means the
     example and the cents do not describe the same amount - a stale attribute, a
     surface this was never meant for - and guessing from there is how you print a
     wrong price. */
  var decimals;
  if (digits === String(Math.round(exampleCents / 100))) {
    decimals = 0;
  } else if (digits === String(exampleCents)) {
    decimals = 2;
  } else {
    return null;
  }

  var decimalSep = '';
  var groupSeps = separators;
  if (decimals === 2) {
    if (!separators) return null; // two decimals and nothing marking them off
    decimalSep = separators.slice(-1);
    groupSeps = separators.slice(0, -1);
  }
  /* No grouping separator in the example means the amount was too small to need
     one. Safe to carry over: the discounted figure is always the smaller of the
     two, so it can never need a group the example did not already have. */
  var groupSep = groupSeps ? groupSeps.slice(-1) : '';

  var rendered = decimals === 2
    ? (cents / 100).toFixed(2)
    : String(Math.round(cents / 100));
  var parts = rendered.split('.');
  var whole = groupSep
    ? parts[0].replace(/(\d)(?=(\d{3})+$)/g, '$1' + groupSep)
    : parts[0];
  var fraction = parts[1] ? decimalSep + parts[1] : '';

  return prefix + whole + fraction + suffix;
};
